import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { useZoomContext } from './ZoomContext'
import { useContentSharing } from './ContentSharingContext'
import { useUI } from './UIContext'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Message, MentorAIResponse } from '../types/mentorTypes'

import {
    createUserMessage,
    createMentorMessage,
    broadcastIfSharer,
    createMentorAIRequest,
} from '../utils/mentorAIHelpers'
import useParticipants from '../hooks/useParticipants'
import { useMentorAIQuery } from '../hooks/useMentorAIQuery'
import { useCourseStudyPlan } from '../hooks/useCourseStudyPlan'
import { useSessionParams } from '../hooks/useSessionParams'
import { NotificationWithCross } from '../components/NotificationWithCross'

interface MentorAIContextValue {
    addUserMessage: (content: string, selectedContext?: string) => void
    addMentorMessage: (content: string) => void
    canInteract: boolean
    selectedContext: string[] | undefined
    setSelectedContext: React.Dispatch<React.SetStateAction<string[] | undefined>>
    isLoading: boolean
    escalated: boolean
    setEscalated: React.Dispatch<React.SetStateAction<boolean>>
    messages: Message[]
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>
    addTextFragment: (text: string) => void
    clearFragments: () => void
    isAskingMentor: boolean
    chatId: string | undefined
}

const MentorAIContext = createContext<MentorAIContextValue | undefined>(undefined)

export const MentorAIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const {
        setSidebarMode,
        sidebarMode,
        setUnReadMessages,
        currentSessionInfo: currentZoomSession,
        commandChannel,
    } = useZoomContext()
    const { isContentShared, isContentSharer, currentStepId } = useContentSharing()
    const { t } = useTranslation('notifications')
    const { showToast } = useUI()
    const { participants } = useParticipants()
    const { _classId: courseId } = useSessionParams()
    const { currentSession: currentContentSession } = useCourseStudyPlan(courseId)
    const [chatId, setChatId] = useState<string>()
    // Track the currently selected context (text selection)
    const [selectedContext, setSelectedContext] = useState<string[] | undefined>()
    // Track if any questions have been escalated
    const [escalated, setEscalated] = useState(false)
    const [messages, setMessages] = useState<Message[]>([])

    // Use our Mentor AI query hook for API integration
    const { askMentor, isLoading, isError, error, history, isAskingMentor } = useMentorAIQuery()

    // Determine if the current user can interact with the MentorAI
    // Only the content sharer can interact when content is being shared
    const canInteract = isContentShared && isContentSharer

    useEffect(() => {
        if (currentZoomSession?.topic && currentContentSession?.id) {
            const newChatId = `${currentZoomSession.topic}-${currentContentSession.id}`
            console.log('📣 Setting chatId:', newChatId)
            setChatId(newChatId)
        }
    }, [currentZoomSession, currentContentSession])

    // Broadcast the sidebar mode change to the content sharer
    useEffect(() => {
        if (!commandChannel) return
        if (sidebarMode === null) {
            broadcastIfSharer({ type: 'content_sharing', action: 'sidebar_close', chatId }, commandChannel, isContentSharer)
        } else {
            broadcastIfSharer(
                { type: 'content_sharing', action: `sidebar_change_${sidebarMode}`, chatId },
                commandChannel,
                isContentSharer
            )
        }
    }, [sidebarMode, commandChannel, isContentSharer])

    useEffect(() => {
        if (history) {
            // console.log('📣 Chat history:', history)
            // Transform history data into Message format
            const transformedMessages: Message[] = history.map((msg, index) => ({
                id: `${index}-${msg.name || 'assistant'}-${Date.now()}`,
                content: msg.content,
                sender: msg.role === 'user' ? 'user' : 'mentor',
                timestamp: Date.now(),
                name: msg.name,
            }))

            // Replace messages instead of appending to avoid duplicates
            setMessages(transformedMessages)
        }
    }, [history])

    const addTextFragment = (text: string) => {
        // Only allow adding fragments if user can interact
        if (!canInteract) {
            const message =
                typeof t === 'function'
                    ? t('mentor.cannotInteract', 'Only the content sharer can ask questions')
                    : 'Only the content sharer can ask questions'

            showToast(message, {
                icon: <FontAwesomeIcon icon={['far', 'robot']} style={{ color: '#888' }} />,
            })
            return
        }
        // Open the mentor sidebar
        setSidebarMode('mentor')
        // Replace the previous fragment with the new one (limit to 1 fragment)
        setSelectedContext([text])

        // Show a confirmation toast
        const toastMessage = selectedContext && selectedContext.length > 0 
            ? t('mentor.fragmentReplaced', 'Fragment replaced in your question')
            : t('mentor.fragmentAdded', 'Fragment added to your question')
        
        showToast(toastMessage, {
            icon: <FontAwesomeIcon icon={['far', 'sparkles']} style={{ color: '#4D0077' }} />,
        })
    }

    const clearFragments = () => {
        setSelectedContext([])
    }

    // Add a user message to the chat
    const addUserMessage = async (content: string) => {
        // If user can't interact, don't add the message
        if (!canInteract || !commandChannel) return

        // Create and add user message
        const userMessage = createUserMessage(content)
        setMessages(prev => [...prev, userMessage])

        // Get the step ID from the Content Sharing context
        const stepId = currentStepId || 'unknown-step'

        if (!chatId) {
            console.error('Chat ID is not set')
            return
        }

        // Create the request with all required information
        const request = createMentorAIRequest(stepId, content, participants, chatId, selectedContext)

        try {
            const response: MentorAIResponse = await askMentor(request)
            // Add the AI's response as a mentor message
            addMentorMessage(response.answer)
            // Update escalation status if needed
            if (response.escalate) {
                setEscalated(true)

                // Notify the user if the question was escalated
                showToast(
                    <NotificationWithCross id="mentor-escalated">
                        <FontAwesomeIcon icon={['far', 'user-headset']} style={{ color: 'white', marginRight: '8px' }} />
                        {t('mentor.escalated', 'Your question has been escalated to a human expert')}
                    </NotificationWithCross>,
                    {
                        id: 'mentor-escalated',
                        duration: Infinity
                    }
                )
                broadcastIfSharer({ type: 'mentor_ai', action: 'new_message', chatId, escalated: true }, commandChannel, isContentSharer)
            }

            // Broadcast the user message to all participants

            // Clear the selected context after use
            if (selectedContext) {
                setSelectedContext(undefined)
            }
        } catch (err) {
            console.error('Failed to get response from Mentor AI:', err)

            // Show error toast to the user
            showToast(t('mentor.apiError', 'Failed to get response from Mentor AI'), {
                icon: <FontAwesomeIcon icon={['far', 'robot']} style={{ color: '#888' }} />,
            })

            // If API fails completely, add a fallback message for better UX
            addMentorMessage(
                'I apologize, but I am having trouble connecting to my knowledge base right now. Please try again later.'
            )

            // Clear the selected context after use
            if (selectedContext) {
                setSelectedContext(undefined)
            }
        }
    }

    // Add a mentor message to the chat
    const addMentorMessage = (content: string) => {
        if (!commandChannel) return

        const mentorMessage = createMentorMessage(content)
        setMessages(prev => [...prev, mentorMessage])

        // If sidebar mode is not 'mentor', set unread messages for mentor
        if (sidebarMode !== 'mentor') {
            setUnReadMessages(prev => {
                // If false, create a new object with mentor set to true
                if (!prev) return { mentor: true }
                // Otherwise update the existing object
                return { ...prev, mentor: true }
            })
        }

        // Broadcast the mentor's response if I'm the content sharer
        broadcastIfSharer({ type: 'mentor_ai', action: 'new_message', chatId }, commandChannel, isContentSharer)
    }

    // Log any API errors but keep working
    if (isError && error) {
        console.error('Mentor AI API error:', error)
    }

    return (
        <MentorAIContext.Provider
            value={{
                messages,
                setMessages,
                addUserMessage,
                addMentorMessage,
                canInteract,
                selectedContext,
                setSelectedContext,
                isLoading,
                escalated,
                setEscalated,
                addTextFragment,
                clearFragments,
                isAskingMentor,
                chatId,
            }}
        >
            {children}
        </MentorAIContext.Provider>
    )
}

export const useMentorAI = (): MentorAIContextValue => {
    const context = useContext(MentorAIContext)
    if (context === undefined) {
        throw new Error('useMentorAI must be used within a MentorAIProvider')
    }
    return context
}
